import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
    UploadSimple,
    LinkSimple,
    Trash,
    FileArrowUp,
    Globe,
    CheckCircle,
    TreeStructure,
    X,
    ArrowsClockwise,
} from "@phosphor-icons/react";

const CATEGORIES = ["Licensing", "Strategy", "Compliance", "Vendor", "Other"];
const SCOPE_TAGS = [
    { v: "both", l: "Both (SMB + Enterprise)" },
    { v: "smb", l: "SMB (≤300 users)" },
    { v: "enterprise", l: "Enterprise (>300 users)" },
];
const MAX_PAGES_HARD = 50;
const MAX_DEPTH_HARD = 2;

const EMPTY_FORM = {
    title: "",
    category: "Strategy",
    scope_tag: "both",
    extraction_date: "",
    summary: "",
    url: "",
    file: null,
    start_url: "",
    title_prefix: "",
    max_pages: 25,
    max_depth: 1,
};

export default function SupplementaryReferences() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [mode, setMode] = useState(null); // null | "upload" | "url" | "crawl"
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);
    const [crawlJob, setCrawlJob] = useState(null);
    const pollRef = useRef(null);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    useEffect(() => () => stopPolling(), []);

    const load = async () => {
        setErr("");
        try {
            const { data } = await api.get("/admin/supplementary-references");
            setItems(data || []);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/supplementary-references");
                if (!cancelled) setItems(data || []);
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setMode(null);
    };

    const onSubmitUpload = async () => {
        if (!form.file) {
            setErr("Please choose a PDF or DOCX file.");
            return;
        }
        if (!form.title.trim()) {
            setErr("Title is required.");
            return;
        }
        if (!form.extraction_date.trim()) {
            setErr("Extraction date is required (e.g. \"May 1, 2026\").");
            return;
        }
        setBusy(true);
        setErr("");
        setMsg("");
        const fd = new FormData();
        fd.append("file", form.file);
        fd.append("title", form.title.trim());
        fd.append("category", form.category);
        fd.append("scope_tag", form.scope_tag);
        fd.append("extraction_date", form.extraction_date.trim());
        if (form.summary.trim()) fd.append("summary", form.summary.trim());
        try {
            const { data } = await api.post(
                "/admin/supplementary-references/upload",
                fd,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setMsg(
                `Added "${data.title}" — ${data.char_count.toLocaleString()} chars extracted from ${data.source_filename}.`
            );
            resetForm();
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const onSubmitUrl = async () => {
        if (!form.title.trim()) {
            setErr("Title is required.");
            return;
        }
        if (!form.url.trim()) {
            setErr("URL is required.");
            return;
        }
        if (!form.extraction_date.trim()) {
            setErr("Extraction date is required (e.g. \"May 1, 2026\").");
            return;
        }
        setBusy(true);
        setErr("");
        setMsg("");
        try {
            const { data } = await api.post("/admin/supplementary-references/url", {
                title: form.title.trim(),
                category: form.category,
                scope_tag: form.scope_tag,
                url: form.url.trim(),
                extraction_date: form.extraction_date.trim(),
                summary: form.summary.trim() || undefined,
            });
            setMsg(
                `Fetched "${data.title}" — ${data.char_count.toLocaleString()} chars from ${data.source_url}.`
            );
            resetForm();
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const pollCrawl = async (jobId) => {
        try {
            const { data } = await api.get(`/admin/crawl-jobs/${jobId}`);
            setCrawlJob(data);
            if (data.status !== "running") {
                stopPolling();
                await load();
                setMsg(
                    `Crawl ${data.status} — saved ${data.saved_count}, skipped ${data.skipped_count}, failed ${data.failed_count} (examined ${data.examined_count}).`
                );
            }
        } catch (e) {
            stopPolling();
            setErr(formatApiError(e));
        }
    };

    const onSubmitCrawl = async () => {
        if (!form.start_url.trim()) {
            setErr("Start URL is required.");
            return;
        }
        if (!form.title_prefix.trim()) {
            setErr("Title prefix is required — every saved page is named '<prefix> — <page title>'.");
            return;
        }
        if (!form.extraction_date.trim()) {
            setErr("Extraction date is required (e.g. \"May 1, 2026\").");
            return;
        }
        const maxPages = Math.max(1, Math.min(MAX_PAGES_HARD, parseInt(form.max_pages, 10) || 25));
        const maxDepth = Math.max(0, Math.min(MAX_DEPTH_HARD, parseInt(form.max_depth, 10) || 1));
        setBusy(true);
        setErr("");
        setMsg("");
        setCrawlJob(null);
        try {
            const { data } = await api.post("/admin/crawl-jobs", {
                start_url: form.start_url.trim(),
                title_prefix: form.title_prefix.trim(),
                category: form.category,
                scope_tag: form.scope_tag,
                extraction_date: form.extraction_date.trim(),
                summary: form.summary.trim() || undefined,
                max_pages: maxPages,
                max_depth: maxDepth,
            });
            setCrawlJob(data);
            setMode(null);
            stopPolling();
            pollRef.current = setInterval(() => pollCrawl(data.id), 2500);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (item) => {
        if (!window.confirm(`Delete "${item.title}" from the supplementary library?`)) return;
        setErr("");
        try {
            await api.delete(`/admin/supplementary-references/${item.id}`);
            setMsg(`Deleted "${item.title}".`);
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const [refreshingId, setRefreshingId] = useState(null);
    const [staleAges, setStaleAges] = useState({});
    useEffect(() => {
        const now = Date.now();
        const next = {};
        for (const it of items) {
            const stamp = it.last_refreshed_at || it.created_at;
            if (!stamp) continue;
            const days = Math.floor((now - new Date(stamp).getTime()) / 86400000);
            if (days >= 365) next[it.id] = days;
        }
        setStaleAges(next);
    }, [items]);
    const onRefresh = async (item) => {
        setErr("");
        setMsg("");
        setRefreshingId(item.id);
        try {
            const { data } = await api.post(`/admin/supplementary-references/${item.id}/refresh`);
            setMsg(
                `Refreshed "${item.title}" — ${data.char_count.toLocaleString()} chars, extraction date now ${data.extraction_date}.`
            );
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setRefreshingId(null);
        }
    };

    return (
        <div className="mt-12 pt-10 border-t border-[#2a2a2a]" data-testid="supplementary-references">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                <div>
                    <div className="overline mb-2">Supplementary references</div>
                    <h2 className="font-display text-3xl">Admin-curated documents &amp; approved web sources</h2>
                    <p className="text-sm text-[#c2c2c2] mt-2 max-w-2xl leading-relaxed">
                        Whitepapers (McKinsey, Info-Tech, Gartner), prior research, or approved URLs.
                        The AI will use these as <em>background context</em> alongside the bundled
                        Microsoft Plan Comparison. They are <strong>not</strong> authoritative on SKU
                        pricing — they shape narrative and benchmarks.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className={`btn-secondary inline-flex items-center gap-2 ${mode === "upload" ? "ring-1 ring-[#2ec5b6]" : ""}`}
                        onClick={() => {
                            setMode(mode === "upload" ? null : "upload");
                            setForm({ ...EMPTY_FORM });
                            setErr("");
                            setMsg("");
                        }}
                        data-testid="supp-add-upload-button"
                    >
                        <FileArrowUp size={14} weight="bold" /> Upload document
                    </button>
                    <button
                        type="button"
                        className={`btn-secondary inline-flex items-center gap-2 ${mode === "url" ? "ring-1 ring-[#2ec5b6]" : ""}`}
                        onClick={() => {
                            setMode(mode === "url" ? null : "url");
                            setForm({ ...EMPTY_FORM });
                            setErr("");
                            setMsg("");
                        }}
                        data-testid="supp-add-url-button"
                    >
                        <Globe size={14} weight="bold" /> Add URL
                    </button>
                    <button
                        type="button"
                        className={`btn-secondary inline-flex items-center gap-2 ${mode === "crawl" ? "ring-1 ring-[#2ec5b6]" : ""}`}
                        onClick={() => {
                            setMode(mode === "crawl" ? null : "crawl");
                            setForm({ ...EMPTY_FORM });
                            setErr("");
                            setMsg("");
                        }}
                        data-testid="supp-add-crawl-button"
                        title="Crawl every page under a URL prefix"
                    >
                        <TreeStructure size={14} weight="bold" /> Crawl section
                    </button>
                </div>
            </div>

            {msg && (
                <div className="block-opportunity mb-3 text-sm" data-testid="supp-msg">
                    <CheckCircle size={14} weight="bold" className="inline mr-1.5 -mt-0.5" />
                    {msg}
                </div>
            )}
            {err && (
                <div className="block-risk mb-3 text-sm" data-testid="supp-error">
                    {err}
                </div>
            )}

            {/* ADD FORMS */}
            {mode && (
                <div className="border border-[#2ec5b6]/40 bg-[#0f1a19] p-5 mb-6" data-testid={`supp-form-${mode}`}>
                    <div className="overline mb-3">
                        {mode === "upload" && "Upload PDF / DOCX"}
                        {mode === "url" && "Add approved URL"}
                        {mode === "crawl" && "Crawl section (same-prefix, depth ≤ 2)"}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {mode === "crawl" ? (
                            <>
                                <div className="md:col-span-2">
                                    <label className="label">Start URL (only pages under this path will be crawled)</label>
                                    <input
                                        className="input-base"
                                        placeholder="https://learn.microsoft.com/en-us/licensing/"
                                        value={form.start_url}
                                        onChange={(e) => setForm({ ...form, start_url: e.target.value })}
                                        data-testid="supp-crawl-start-input"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="label">Title prefix (each saved page becomes &quot;Prefix — Page title&quot;)</label>
                                    <input
                                        className="input-base"
                                        placeholder="MS Licensing Docs"
                                        value={form.title_prefix}
                                        onChange={(e) => setForm({ ...form, title_prefix: e.target.value })}
                                        data-testid="supp-crawl-prefix-input"
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="md:col-span-2">
                                <label className="label">Title (admin display name)</label>
                                <input
                                    className="input-base"
                                    placeholder='e.g. "Info-Tech: Optimize M365 Licensing 2026"'
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    data-testid="supp-title-input"
                                />
                            </div>
                        )}
                        <div>
                            <label className="label">Category</label>
                            <select
                                className="input-base"
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                data-testid="supp-category-select"
                            >
                                {CATEGORIES.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="label">Applies to</label>
                            <select
                                className="input-base"
                                value={form.scope_tag}
                                onChange={(e) => setForm({ ...form, scope_tag: e.target.value })}
                                data-testid="supp-scope-select"
                            >
                                {SCOPE_TAGS.map((s) => (
                                    <option key={s.v} value={s.v}>{s.l}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="label">Extraction date (e.g. &quot;May 1, 2026&quot;)</label>
                            <input
                                className="input-base"
                                placeholder="May 1, 2026"
                                value={form.extraction_date}
                                onChange={(e) => setForm({ ...form, extraction_date: e.target.value })}
                                data-testid="supp-date-input"
                            />
                        </div>
                        {mode === "upload" && (
                            <div>
                                <label className="label">PDF or DOCX file (≤25 MB)</label>
                                <label className="input-base inline-flex items-center gap-3 cursor-pointer hover:bg-[#1f1f1f]" style={{ paddingTop: 12, paddingBottom: 12 }}>
                                    <UploadSimple size={16} weight="bold" className="text-[#2ec5b6]" />
                                    <span className="text-[#c2c2c2] text-sm truncate">
                                        {form.file ? form.file.name : "Choose file…"}
                                    </span>
                                    <input
                                        type="file"
                                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        className="hidden"
                                        onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                                        data-testid="supp-file-input"
                                    />
                                </label>
                            </div>
                        )}
                        {mode === "url" && (
                            <div>
                                <label className="label">URL (https://…)</label>
                                <input
                                    className="input-base"
                                    placeholder="https://www.example.com/whitepaper"
                                    value={form.url}
                                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                                    data-testid="supp-url-input"
                                />
                            </div>
                        )}
                        {mode === "crawl" && (
                            <>
                                <div>
                                    <label className="label">Max pages (1–{MAX_PAGES_HARD})</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={MAX_PAGES_HARD}
                                        className="input-base"
                                        value={form.max_pages}
                                        onChange={(e) => setForm({ ...form, max_pages: e.target.value })}
                                        data-testid="supp-crawl-maxpages-input"
                                    />
                                </div>
                                <div>
                                    <label className="label">Max depth (0–{MAX_DEPTH_HARD}; 0 = start URL only)</label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={MAX_DEPTH_HARD}
                                        className="input-base"
                                        value={form.max_depth}
                                        onChange={(e) => setForm({ ...form, max_depth: e.target.value })}
                                        data-testid="supp-crawl-maxdepth-input"
                                    />
                                </div>
                            </>
                        )}
                        <div className="md:col-span-2">
                            <label className="label">Short admin summary (optional, shown on the card)</label>
                            <textarea
                                rows={2}
                                className="input-base"
                                placeholder="One-sentence note on why this source is included."
                                value={form.summary}
                                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                                data-testid="supp-summary-input"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-4">
                        <button
                            type="button"
                            className="btn-ghost"
                            onClick={resetForm}
                            disabled={busy}
                            data-testid="supp-cancel-button"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary inline-flex items-center gap-2"
                            onClick={
                                mode === "upload"
                                    ? onSubmitUpload
                                    : mode === "url"
                                        ? onSubmitUrl
                                        : onSubmitCrawl
                            }
                            disabled={busy}
                            data-testid="supp-submit-button"
                        >
                            {mode === "upload" && <UploadSimple size={14} weight="bold" />}
                            {mode === "url" && <LinkSimple size={14} weight="bold" />}
                            {mode === "crawl" && <TreeStructure size={14} weight="bold" />}
                            {busy
                                ? mode === "upload"
                                    ? "Uploading…"
                                    : mode === "url"
                                        ? "Fetching…"
                                        : "Starting crawl…"
                                : mode === "upload"
                                    ? "Upload & extract"
                                    : mode === "url"
                                        ? "Fetch & save"
                                        : "Start crawl"}
                        </button>
                    </div>
                </div>
            )}

            {/* CRAWL PROGRESS PANEL */}
            {crawlJob && (
                <div
                    className="border border-[#2ec5b6]/40 bg-[#0a1413] p-5 mb-6"
                    data-testid="supp-crawl-progress"
                >
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                            <div className="overline mb-1">
                                Crawl {crawlJob.status === "running" ? "in progress" : crawlJob.status}
                            </div>
                            <div className="font-display text-base text-white break-all">
                                {crawlJob.start_url}
                            </div>
                            <div className="text-[12px] text-[#9a9a9a] mt-1">
                                cap {crawlJob.max_pages} pages · depth {crawlJob.max_depth}
                            </div>
                        </div>
                        {crawlJob.status !== "running" && (
                            <button
                                type="button"
                                className="btn-ghost p-2"
                                onClick={() => {
                                    setCrawlJob(null);
                                    stopPolling();
                                }}
                                title="Dismiss"
                                data-testid="supp-crawl-dismiss"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center mb-3">
                        <div className="border border-[#2a2a2a] py-2">
                            <div className="text-[11px] uppercase tracking-widest text-[#7a7a7a]">Saved</div>
                            <div className="font-mono text-xl text-[#2ec5b6]">{crawlJob.saved_count}</div>
                        </div>
                        <div className="border border-[#2a2a2a] py-2">
                            <div className="text-[11px] uppercase tracking-widest text-[#7a7a7a]">Skipped</div>
                            <div className="font-mono text-xl text-[#d4d4d4]">{crawlJob.skipped_count}</div>
                        </div>
                        <div className="border border-[#2a2a2a] py-2">
                            <div className="text-[11px] uppercase tracking-widest text-[#7a7a7a]">Failed</div>
                            <div className="font-mono text-xl text-[#d4d4d4]">{crawlJob.failed_count}</div>
                        </div>
                        <div className="border border-[#2a2a2a] py-2">
                            <div className="text-[11px] uppercase tracking-widest text-[#7a7a7a]">Examined</div>
                            <div className="font-mono text-xl text-[#d4d4d4]">{crawlJob.examined_count}</div>
                        </div>
                    </div>
                    <div className="text-[12px] text-[#9a9a9a] mb-2">
                        Queue: <span className="font-mono text-[#d4d4d4]">{crawlJob.queue_size}</span>
                        {" · "}
                        Status: <span className="font-mono text-[#d4d4d4]">{crawlJob.status}</span>
                    </div>
                    {Array.isArray(crawlJob.log) && crawlJob.log.length > 0 && (
                        <div className="bg-[#050505] border border-[#1f1f1f] p-3 max-h-44 overflow-y-auto font-mono text-[11px] leading-relaxed">
                            {crawlJob.log.slice(-12).map((row, idx) => (
                                <div
                                    key={`${row.url || "row"}-${idx}-${row.status || ""}`}
                                    className={
                                        row.status === "saved"
                                            ? "text-[#2ec5b6]"
                                            : row.status === "failed"
                                                ? "text-[#ff6b6b]"
                                                : "text-[#9a9a9a]"
                                    }
                                >
                                    [{row.status}] {row.url}
                                    {row.reason ? ` — ${row.reason}` : ""}
                                    {row.chars ? ` — ${row.chars.toLocaleString()} chars` : ""}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* LIST */}
            {loading ? (
                <div className="text-[#c2c2c2] text-sm uppercase tracking-widest py-6">Loading…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-[#3a3a3a] bg-[#0a0a0a] p-8 text-center" data-testid="supp-empty-state">
                    <div className="text-[#9a9a9a] text-sm leading-relaxed max-w-md mx-auto">
                        No supplementary references yet. Click <strong className="text-white">Upload document</strong> or <strong className="text-white">Add URL</strong> above to give the AI extra context beyond the bundled Microsoft Plan Comparison.
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="supp-list">
                    {items.map((it) => {
                        const ageDays = staleAges[it.id] || 0;
                        const isStale = ageDays >= 365;
                        return (
                        <div
                            key={it.id}
                            className={`border bg-[#141414] p-5 ${isStale ? "border-[#d9a441]/60" : "border-[#2a2a2a]"}`}
                            data-testid={`supp-card-${it.id}`}
                        >
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex-1">
                                    <div className="overline mb-1">{it.category} · {it.scope_tag === "both" ? "SMB + Enterprise" : it.scope_tag.toUpperCase()}</div>
                                    <div className="font-display text-lg text-white leading-tight">
                                        {it.title}
                                    </div>
                                    {isStale && (
                                        <div
                                            className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 bg-[#d9a441]/15 border border-[#d9a441]/50 text-[#e6b863] text-[11px] uppercase tracking-widest font-semibold"
                                            data-testid={`supp-stale-badge-${it.id}`}
                                            title={`Last refreshed ${ageDays} days ago — consider refreshing`}
                                        >
                                            ● {ageDays} days old — consider refreshing
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    {it.source_url && (
                                        <button
                                            type="button"
                                            className="btn-ghost p-2"
                                            onClick={() => onRefresh(it)}
                                            disabled={refreshingId === it.id}
                                            title="Re-fetch this URL and update content in place"
                                            data-testid={`supp-refresh-${it.id}`}
                                        >
                                            <ArrowsClockwise
                                                size={14}
                                                className={refreshingId === it.id ? "animate-spin" : ""}
                                            />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn-ghost p-2"
                                        onClick={() => onDelete(it)}
                                        title="Delete reference"
                                        data-testid={`supp-delete-${it.id}`}
                                    >
                                        <Trash size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="text-[12px] text-[#9a9a9a] space-y-0.5">
                                <div>
                                    <span className="text-[#7a7a7a]">Type:</span>{" "}
                                    <span className="text-[#d4d4d4] uppercase tracking-wide">{it.ref_type}</span>
                                    {" · "}
                                    <span className="text-[#7a7a7a]">Extracted:</span>{" "}
                                    <span className="text-white">{it.extraction_date}</span>
                                </div>
                                {it.source_filename && (
                                    <div>
                                        <span className="text-[#7a7a7a]">File:</span>{" "}
                                        <span className="font-mono text-[#d4d4d4] break-all">{it.source_filename}</span>
                                    </div>
                                )}
                                {it.source_url && (
                                    <div>
                                        <span className="text-[#7a7a7a]">URL:</span>{" "}
                                        <a
                                            href={it.source_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[#2ec5b6] hover:underline break-all"
                                        >
                                            {it.source_url}
                                        </a>
                                    </div>
                                )}
                                <div>
                                    <span className="text-[#7a7a7a]">Chars indexed:</span>{" "}
                                    <span className="font-mono text-[#d4d4d4]">{(it.char_count || 0).toLocaleString()}</span>
                                </div>
                                <div>
                                    <span className="text-[#7a7a7a]">Added:</span>{" "}
                                    {new Date(it.created_at).toLocaleString()} by{" "}
                                    <span className="text-[#d4d4d4]">{it.created_by}</span>
                                </div>
                                {it.last_refreshed_at && (
                                    <div>
                                        <span className="text-[#7a7a7a]">Last refreshed:</span>{" "}
                                        <span className="text-[#2ec5b6]">
                                            {new Date(it.last_refreshed_at).toLocaleString()}
                                        </span>
                                        {it.last_refreshed_by ? (
                                            <>
                                                {" "}
                                                by{" "}
                                                <span className="text-[#d4d4d4]">
                                                    {it.last_refreshed_by}
                                                </span>
                                            </>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            {it.summary && (
                                <div className="mt-3 pt-3 border-t border-[#2a2a2a] text-[13px] text-[#c2c2c2] leading-relaxed">
                                    {it.summary}
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
